import React from 'react';

import {
  Box,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import {
  errorUtil,
  fields,
  formUtil
} from 'iron-forge-react-modules';

import { useForm } from 'react-hook-form';
import { FormModal } from 'modules/layer00';
import * as dataService from './dataService';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();

export interface IProps {
  orderId: string;

  onClose: () => void;
  onSubmit: () => void;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.orderId);
  const [saveAddress, saveAddressTuple] = dataService.useSaveOrder(props.orderId);
  const form = useForm<types.IFormState>({
    defaultValues: {
      billingAddress: {
        addressee: '',
        street1: '',
        street2: '',
        city: '',
        usState: '',
        postalCode: '',
        countryCode: '',
        company: '',
        phone: ''
      },
      shippingAddress: {
        addressee: '',
        street1: '',
        street2: '',
        city: '',
        usState: '',
        postalCode: '',
        countryCode: '',
        company: '',
        phone: ''
      }
    }
  });

  formUtil.useSetFormFields(queryTuple, form.setValue, [
    'billingAddress',
    'shippingAddress'
  ]);

  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      await errorUtil.errorWrap(async () => {
        await saveAddress(formState);
        props.onSubmit();
      }, {
        INVALID_ARGS: "Invalid arguments."
      });
    },
    [
      props,
      saveAddress
    ]
  );

  const loading = queryTuple.loading || saveAddressTuple.loading;

  const defaultTextFieldProps = {
    disableDefaultMargins: true,
    isDisabled: loading,
    formControl: form.control
  };

  return (
    <FormModal.FC
      title="Edit Address"
      isLoading={loading}
      onClose={props.onClose}
      onSubmit={form.handleSubmit(processSubmit)}>

      <Typography variant="h6">Billing Address</Typography>
      <Grid container spacing={1}>
        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            fieldKey="billingAddress.addressee"
            label="Addressee" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            isRequired
            fieldKey="billingAddress.street1"
            label="Street 1" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            fieldKey="billingAddress.street2"
            label="Street 2" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            isRequired
            fieldKey="billingAddress.city"
            label="City" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            isRequired
            fieldKey="billingAddress.usState"
            label="State" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            isRequired
            fieldKey="billingAddress.postalCode"
            label="Postal Code" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            isRequired
            fieldKey="billingAddress.countryCode"
            label="Country Code" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            fieldKey="billingAddress.company"
            label="Company" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            fieldKey="billingAddress.phone"
            label="Phone" />
        </Grid>
      </Grid>

      <Box my={2}>
        <Divider />
      </Box>

      <Typography variant="h6">Shipping Address</Typography>
      <Grid container spacing={1}>
        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            fieldKey="shippingAddress.addressee"
            label="Addressee" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            isRequired
            fieldKey="shippingAddress.street1"
            label="Street 1" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            fieldKey="shippingAddress.street2"
            label="Street 2" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            isRequired
            fieldKey="shippingAddress.city"
            label="City" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            isRequired
            fieldKey="shippingAddress.usState"
            label="State" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            isRequired
            fieldKey="shippingAddress.postalCode"
            label="Postal Code" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            isRequired
            fieldKey="shippingAddress.countryCode"
            label="Country Code" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            fieldKey="shippingAddress.company"
            label="Company" />
        </Grid>

        <Grid item>
          <ControlledTextField
            {...defaultTextFieldProps}
            fieldKey="shippingAddress.phone"
            label="Phone" />
        </Grid>
      </Grid>

    </FormModal.FC>
  );
};
